/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-generated-report/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GeneratedReport, GeneratedReportStatus } from '@interfaces/report-templates.interface';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns/dist/npm/DropDownList/DropDownListProps';
import cn from 'clsx';
import React from 'react';

import { Controller, useForm } from 'react-hook-form';
// eslint-disable-next-line max-len
import { EditGeneratedReportProps } from '@components/shared/edit-generated-report/edit-generated-report.interface';
import { useTranslation, useDateTime, useLocalState } from '@hooks/index';

import { validateFields } from '@utils/validation';
import Button from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from '@components/core/dialog';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import SelectWithCategories from '@components/core/select-with-categories';
import TextField from '@components/core/text-field';
import { MODAL_CLOSE_ACTIONS } from '@utils/constants';
import NotificationService from '@utils/notification';
import { useMutationGeneratedReport } from '@apis/bff-api/report-templates';
import Loading from '@components/core/loading';

import './styles.scss';

const EditGeneratedReport = ({ onClose, report }: EditGeneratedReportProps) => {
    const localState = useLocalState();
    const { t } = useTranslation();
    const date = useDateTime();

    const statuses = [
        { id: GeneratedReportStatus.Ready, text: t('ready') },
        { id: GeneratedReportStatus.Obsolete, text: t('obsolete') },
    ];

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isValid, errors, isDirty },
    } = useForm<Partial<GeneratedReport>>({
        mode: 'all',
        defaultValues: {
            status: report.status,
            comments: report.comments,
        },
    });

    const { trigger: putGeneratedReport, isMutating } = useMutationGeneratedReport();

    const statusValue = watch('status');

    const studyNumber = localState.state?.selectedStudy?.studyNumber;
    const submit = (data: Partial<GeneratedReport>) => {
        const reportToSubmit = {
            id: report.id,
            orgKey: report.orgKey,
            reportTemplateId: report.reportTemplateId,
            pbiDatasetId: report.pbiDatasetId,
            reportName: report.reportName,
            reportVersion: report.reportVersion,
            studyId: report.studyId,
            status: data.status,
            module: report.module,
            riskReviewId: report.riskReviewId,
            onlyIncludeReviewed: report.onlyIncludeReviewed,
            datasetId: report.datasetId,
            comparisonDatasetId: report.comparisonDatasetId,
            recordId: report.recordId,
            signatories: report.signatories,
            includeSignatures: report.includeSignatures,
            includeNameAndVersion: report.includeNameAndVersion,
            includeReviews: report.includeReviews,
            includeNotApplicableRisks: report.includeNotApplicableRisks,
            includeInternalRisks: report.includeInternalRisks,
            includeComparisonDataset: report.includeComparisonDataset,
            comments: data.comments,
            studyName: studyNumber,
        };

        putGeneratedReport(reportToSubmit)
            .then(() => {
                onClose(MODAL_CLOSE_ACTIONS.SUCCESS);
                NotificationService.showNotification(
                    t('reports:reportSuccessfulUpdated', {
                        id: report?._itemId ?? report.recordId ?? report.id,
                    }),
                );
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    const validate = (value: string, field: keyof GeneratedReport, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);

        return error[field];
    };

    const handleChange = (onChange: (value: string) => void) => (event: DropDownListChangeEvent) => {
        const fieldValue = event.target.value;
        if (fieldValue === GeneratedReportStatus.Ready) {
            setValue('comments', '');
        }
        onChange(fieldValue);
    };

    return (
        <CustomDialog onClose={onClose} fullWidth maxWidth="md" scroll="paper" open className="edit-cv-dialog">
            <DialogTitle id="edit-signatory-modal-title">
                {t('reports:editReportModal', { id: report?._itemId ?? report?.recordId ?? report.id })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            {isMutating && <Loading float />}
            <DialogContent className="edit-signatory-dialog-content">
                <p className="k-mb-4">
                    <span className="k-font-medium k-pr-1">{t('reports:generatedDate')}: </span>
                    {date.display(report?.createdDate, 'DD-MMM-YYYY HH:mm UTC')}
                </p>

                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <Controller
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('ra:requiredFieldError'),
                                },
                            }}
                            name="status"
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectWithCategories
                                    showWarning={false}
                                    customClassName="status"
                                    fieldSelect="status"
                                    label={`${t('ra:status')}*`}
                                    filterable={false}
                                    onChange={() => handleChange(onChange)}
                                    labelField="status"
                                    dataArray={statuses}
                                    customId="select-outlined-type"
                                    currentState={value}
                                    error={error?.message}
                                />
                            )}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <div className="form-control-type">
                        <Controller
                            control={control}
                            name="comments"
                            rules={{
                                required: {
                                    value: statusValue === GeneratedReportStatus.Obsolete,
                                    message: t('ra:requiredFieldError'),
                                },
                                validate: {
                                    maxLength: (value) => validate(value ?? '', 'comments', 1200) ?? undefined,
                                },
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <div className={cn('form-control-type')}>
                                    <TextField
                                        label={
                                            statusValue === GeneratedReportStatus.Obsolete
                                                ? `${t('ra:comments')}*`
                                                : t('ra:comments')
                                        }
                                        id="edit-report-comments"
                                        data-testId="edit-report-comments"
                                        fullWidth
                                        multiline
                                        minRows={5}
                                        value={value}
                                        onChange={onChange}
                                        placeholder={t('ra:enterComments')}
                                        helperText={
                                            <CharactersRemaining
                                                id="edit-report-dialog-comments"
                                                length={value?.length ?? 0}
                                                max={1200}
                                                error={error?.message}
                                            />
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!error?.message}
                                        disabled={statusValue === GeneratedReportStatus.Ready}
                                    />
                                </div>
                            )}
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions className="actions-info-wrapper-edit-cv actions-add-risk assign-items">
                <ModalUpdatedInfo item={{ ...report, created: report.createdDate, updated: report.updatedDate }} />
                <Button
                    className="edit-report-btn"
                    id="edit-report-btn"
                    disabled={
                        !isValid ||
                        !isDirty ||
                        Object.keys(errors).filter((item) => errors[item as keyof GeneratedReport]).length > 0 ||
                        isMutating
                    }
                    text="update"
                    onClick={handleSubmit(submit)}
                    data-testid="submit-button"
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditGeneratedReport;
